import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, CircularProgress } from "@mui/material";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { toast } from "react-toastify";
import RunPanel from "./RunPanel";
import RunPicker from "./RunPicker";
import { zb, inr, when } from "./api";
import styles from "./ZohoBilling.module.css";

const PROBLEM = (s) => s === "ERROR" || s.startsWith("BLOCKED") || s.endsWith("AMOUNT_MISMATCH");

function Stat({ label, value, tone }) {
  return (
    <div className={`${styles.stat} ${tone ? styles[`stat_${tone}`] : ""}`}>
      <div className={styles.statValue} title={typeof value === "string" ? value : undefined}>
        {value}
      </div>
      <div className={styles.statLabel}>{label}</div>
    </div>
  );
}

function StatementSummary({ statement }) {
  if (!statement) return null;
  return (
    <div className={styles.statRow}>
      <Stat
        label="Statement period"
        value={
          statement.periodFrom
            ? dateRange(statement.periodFrom, statement.periodTo)
            : dateRange(statement.firstDate, statement.lastDate)
        }
      />
      <Stat label="Invoices listed" value={statement.invoices} />
      <Stat label="Credit / debit notes" value={statement.creditNotes} />
      <Stat label="Total of invoices listed" value={inr(statement.invoiceTotal)} />
      <Stat label="Last invoice" value={statement.lastInvoice || "—"} />
    </div>
  );
}

// What is still owed: the statement's closing balance (A + B − C) next to what
// Zoho shows as unpaid on Ingram's bills right now. Read live from Zoho
// (read-only) whenever the run is opened, since it changes as credits and
// payments are applied.
function RemainingAmount({ runId, busy }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await zb(`/runs/${runId}/balances`));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => {
    if (!busy) load();
  }, [busy, load]);

  const st = data && data.statement;
  const z = data && data.zoho;
  const closing = st && st.closingBalance;
  const diff = z && closing != null ? +(z.outstandingBalance - closing).toFixed(2) : null;
  const agrees = diff != null && Math.abs(diff) < 1;

  return (
    <>
      <div className={styles.tableHead} style={{ marginTop: 16 }}>
        <div className={styles.sectionLabel} style={{ margin: 0 }}>
          Remaining amount — statement vs Zoho Books
        </div>
        <span className={styles.small}>
          {loading ? (
            <>
              <CircularProgress size={10} sx={{ mr: 0.5, verticalAlign: "middle" }} /> Reading Zoho…
            </>
          ) : data ? (
            `Checked in Zoho at ${when(data.checkedAt)} · `
          ) : null}
          {!loading && (
            <button type="button" className={styles.linkBtn} onClick={load}>
              Refresh
            </button>
          )}
        </span>
      </div>
      {error && <div className={styles.errorBox}>{error}</div>}
      {st && closing == null && (
        <div className={styles.muted}>The statement file has no closing-balance line, so only the Zoho side is shown.</div>
      )}
      {st && closing != null && (
        <div className={styles.statRow}>
          <Stat label="Opening balance (A)" value={inr(st.openingBalance)} />
          <Stat label="This period's invoices (B)" value={inr(st.periodDebits)} />
          <Stat label="This period's payments / credits (C)" value={inr(st.periodCredits)} />
          <Stat label={`Remaining per statement, as on ${fmtDate(st.closingDate)}`} value={inr(closing)} tone="info" />
        </div>
      )}
      {z && (
        <div className={styles.statRow}>
          <Stat
            label={`Unpaid in Zoho — all Ingram bills (${z.outstandingBills} bill(s))`}
            value={inr(z.outstandingBalance)}
            tone={diff == null ? "" : agrees ? "good" : "warn"}
          />
          <Stat label={`This statement's bills in Zoho (${z.statementBillsInZoho})`} value={inr(z.statementBillsTotal)} />
          <Stat
            label={`…of which still unpaid (${z.statementBillsUnpaid} bill(s))`}
            value={inr(z.statementBillsBalance)}
            tone={z.statementBillsBalance > 0.001 ? "warn" : "good"}
          />
        </div>
      )}
      {diff != null && (
        <div className={agrees ? styles.okBox : styles.warnBox}>
          {agrees
            ? "Zoho's unpaid Ingram balance matches the statement's remaining amount."
            : `Zoho's unpaid Ingram balance is ${inr(Math.abs(diff))} ${diff > 0 ? "more" : "less"} than the statement's remaining amount. Post anything missing, apply credits and payments on the “Payments & credits” tab, then refresh.`}
        </div>
      )}
    </>
  );
}

// Ingram issues a new IM SKU every month for the same product; one vendor-part
// mapping covers all of them, so show one row per vendor part.
function groupUnmapped(rows) {
  const groups = new Map();
  for (const s of rows) {
    const generic = !s.vendPart || s.vendPart === "GWS_ENTITLEMENT";
    const key = generic ? `im:${s.imSku}` : `vp:${s.vendPart}`;
    const g = groups.get(key);
    if (!g) groups.set(key, { ...s, imSkus: [s.imSku], invoiceExamples: [...(s.invoiceExamples || [])] });
    else {
      g.imSkus.push(s.imSku);
      g.occurrences += s.occurrences;
      g.invoiceExamples.push(...(s.invoiceExamples || []));
    }
  }
  return [...groups.values()];
}

function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
}

// "17 Sep – 15 Oct 2026" (the year once when both dates share it).
function dateRange(a, b) {
  if (!a || !b) return "—";
  const fmt = (d, withYear) =>
    new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", ...(withYear ? { year: "numeric" } : {}) });
  const sameYear = a.slice(0, 4) === b.slice(0, 4);
  return `${fmt(a, !sameYear)} – ${fmt(b, true)}`;
}

function UnmappedSkus({ rows: raw, onMap }) {
  const rows = groupUnmapped(raw);
  return (
    <div className={styles.warnBox}>
      <strong>
        {rows.length} product(s) have no Zoho item yet ({raw.length} SKU variant(s)) — nothing is posted until they are
        mapped.
      </strong>
      <table className={styles.table} style={{ marginTop: 10 }}>
        <thead>
          <tr>
            <th>Vendor part</th>
            <th>IM SKU(s)</th>
            <th>Description</th>
            <th>Unit price</th>
            <th>Seen</th>
            <th>Example invoice</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={`${s.imSku}|${s.vendPart}`}>
              <td className={styles.mono}>{s.vendPart || "—"}</td>
              <td className={styles.mono}>{s.imSkus.join(", ")}</td>
              <td>{s.description}</td>
              <td>{inr(s.sampleUnitPrice)}</td>
              <td>{s.occurrences}×</td>
              <td className={styles.mono}>{(s.invoiceExamples || [])[0]}</td>
              <td>
                <Button size="small" onClick={() => onMap(s)} sx={{ textTransform: "none", fontWeight: 700 }}>
                  Map
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className={styles.muted} style={{ marginTop: 8 }}>
        After mapping them in the SKU mapping tab, click “Preview again”.
      </div>
    </div>
  );
}

function BillsTable({ rows, posted }) {
  const [onlyProblems, setOnlyProblems] = useState(true);
  const problems = rows.filter(
    (b) => PROBLEM(b.status) || (!b.customerMatched && !b.status.startsWith("ALREADY")),
  );
  const shown = onlyProblems ? problems : rows;
  return (
    <div style={{ marginTop: 14 }}>
      <div className={styles.tableHead}>
        <strong>Bills</strong>
        <label className={styles.muted}>
          <input type="checkbox" checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} /> Only
          rows that need a look ({problems.length})
        </label>
      </div>
      {shown.length === 0 ? (
        <div className={styles.muted}>Nothing to show.</div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Status</th>
                <th>Statement</th>
                <th>PDF total</th>
                {posted ? <th>Zoho total</th> : <th>Unpaid in Zoho</th>}
                <th>End customer</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {shown.slice(0, 500).map((b) => (
                <tr key={b.invoiceNumber}>
                  <td className={styles.mono}>{b.invoiceNumber}</td>
                  <td>{b.status}</td>
                  <td>{inr(b.statementAmount)}</td>
                  <td>{inr(b.parsedTotal)}</td>
                  {posted ? <td>{inr(b.postedTotal)}</td> : <td>{b.zohoBalance != null ? inr(b.zohoBalance) : "—"}</td>}
                  <td>
                    {b.status.startsWith("ALREADY") ? "" : b.endCustomer || "—"}
                    {!b.customerMatched && !b.status.startsWith("ALREADY") && (
                      <span className={styles.tagWarn}>customer not matched</span>
                    )}
                  </td>
                  <td className={styles.small}>
                    {[b.error, (b.fixes || []).join(", "), b.roundOff ? `round-off ${b.roundOff}` : ""]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CreditNotesTable({ rows }) {
  if (!rows || !rows.length) return null;
  return (
    <div style={{ marginTop: 14 }}>
      <strong>Credit notes → vendor credits</strong>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Credit note</th>
              <th>Status</th>
              <th>Date</th>
              <th>Amount</th>
              <th>Original bill</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.cnNumber}>
                <td className={styles.mono}>{c.cnNumber}</td>
                <td>{c.status}</td>
                <td>{c.cnDate || "—"}</td>
                <td>{inr(c.grandTotal)}</td>
                <td className={styles.mono}>
                  {c.originalInvoice || "—"}
                  {c.originalInvoice && !c.originalBillFound && c.status !== "ALREADY_EXISTS" && (
                    <span className={styles.tagWarn}>bill not in Zoho</span>
                  )}
                </td>
                <td className={styles.small}>{c.error || c.note || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function IngramRunBody({ run, onMap }) {
  const p = run.preview || {};
  const r = run.result;
  const counts = (r ? r.billCounts : p.billCounts) || {};
  const bills = (r ? r.bills : p.bills) || [];
  const notMatched = bills.filter((b) => !b.customerMatched && !b.status.startsWith("ALREADY")).length;
  return (
    <>
      <StatementSummary statement={run.input?.statement} />
      <RemainingAmount runId={run.id} busy={["previewing", "posting"].includes(run.status)} />
      {run.status === "needs_mapping" && <UnmappedSkus rows={p.unmapped || []} onMap={onMap} />}
      {p.discovery?.errors?.length > 0 && (
        <div className={styles.warnBox}>
          {p.discovery.errors.length} invoice(s) could not be downloaded or read:{" "}
          {p.discovery.errors
            .slice(0, 6)
            .map((e) => e.invoiceNumber)
            .join(", ")}
          . They are left out of posting; preview again to retry them.
        </div>
      )}
      {(p.bills || r) && (
        <>
          <div className={styles.sectionLabel}>{r ? "Posting result" : "Preview — nothing has been posted"}</div>
          <div className={styles.statRow}>
            {r ? (
              <>
                <Stat label="Posted" value={counts.POSTED || 0} tone="good" />
                <Stat label="Already in Zoho (skipped)" value={counts.ALREADY_EXISTS_REVIEW || 0} />
                <Stat label="Blocked" value={counts.BLOCKED_needs_mapping || 0} tone={counts.BLOCKED_needs_mapping ? "warn" : ""} />
                <Stat label="Errors" value={counts.ERROR || 0} tone={counts.ERROR ? "bad" : ""} />
              </>
            ) : (
              <>
                <Stat label="Would post" value={(counts.WOULD_POST || 0) + (counts.WOULD_POST_AMOUNT_MISMATCH || 0)} tone="info" />
                <Stat
                  label="Amount ≠ statement"
                  value={counts.WOULD_POST_AMOUNT_MISMATCH || 0}
                  tone={counts.WOULD_POST_AMOUNT_MISMATCH ? "warn" : ""}
                />
                <Stat label="Already in Zoho (will be skipped)" value={counts.ALREADY_IN_ZOHO || 0} />
                <Stat label="Blocked" value={counts.BLOCKED_needs_mapping || 0} tone={counts.BLOCKED_needs_mapping ? "warn" : ""} />
                <Stat label="Errors" value={counts.ERROR || 0} tone={counts.ERROR ? "bad" : ""} />
              </>
            )}
            <Stat label="End customer not matched" value={notMatched} />
          </div>
          {!r && (
            <div className={styles.muted}>
              “Would post” counts only invoices that are not in Zoho yet; the rest are listed as already in Zoho and are
              never posted twice. A bill whose end customer isn’t matched to a Zoho contact is still posted, just without
              the customer link.
            </div>
          )}
          <BillsTable rows={bills} posted={Boolean(r)} />
          <CreditNotesTable rows={r ? r.creditNotes : p.creditNotes} />
        </>
      )}
    </>
  );
}

export default function IngramTab({ runs, activeRunId, onChanged, onMapSku }) {
  const ingramRuns = useMemo(() => runs.filter((r) => r.kind === "ingram"), [runs]);
  const [selected, setSelected] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const current = selected || (ingramRuns[0] && ingramRuns[0].id);

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("statement", file);
      const data = await zb("/ingram/upload", { method: "POST", form });
      toast.success("Statement uploaded. Check the summary, then run the preview.");
      setFile(null);
      setSelected(data.run.id);
      onChanged();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <div className={styles.card}>
        <div className={styles.cardTitle}>Upload the Ingram account statement</div>
        <div className={styles.muted}>
          The <strong>.xlsx</strong> export is the cleanest source; <strong>.pdf</strong> works too. Always use the newest
          statement. Uploading posts nothing: you get a preview first.
        </div>
        <div className={styles.uploadRow}>
          <label className={styles.fileBtn}>
            <UploadFileOutlinedIcon fontSize="small" />
            {file ? file.name : "Choose statement…"}
            <input type="file" accept=".xlsx,.xls,.pdf" hidden onChange={(e) => setFile(e.target.files[0] || null)} />
          </label>
          <Button
            className={styles.primaryBtn}
            variant="contained"
            disabled={!file || uploading}
            onClick={upload}
          >
            {uploading ? <CircularProgress size={16} color="inherit" /> : "Upload"}
          </Button>
        </div>
      </div>

      <RunPicker runs={ingramRuns} current={current} onSelect={setSelected} activeRunId={activeRunId} />

      {current && (
        <RunPanel
          key={current}
          runId={current}
          onChanged={onChanged}
          previewLabel="Run preview"
          canPreview={() => true}
          post={(run) => {
            const p = run.preview || {};
            const bills = (p.bills || []).filter((b) => b.status.startsWith("WOULD_POST")).length;
            const cns = (p.creditNotes || []).filter((c) => c.status === "WOULD_CREATE").length;
            if (!bills && !cns) return null;
            return {
              label: "Post to Zoho Books",
              title: "Post to Zoho Books (live)?",
              body: (
                <>
                  <p>
                    This creates up to <strong>{bills}</strong> vendor bill(s)
                    {cns ? (
                      <>
                        {" "}
                        and <strong>{cns}</strong> vendor credit(s)
                      </>
                    ) : null}{" "}
                    under Ingram in Zoho Books and attaches each invoice PDF.
                  </p>
                  <p>
                    Only the invoices in this preview are posted. Bills that already exist are skipped. Errors are retried
                    twice. Payment matching is a separate step on the “Payments &amp; credits” tab.
                  </p>
                </>
              ),
            };
          }}
        >
          {(run) => <IngramRunBody run={run} onMap={onMapSku} />}
        </RunPanel>
      )}
    </>
  );
}
